import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Download } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, API, formatApiError } from "@/lib/api";

const USER_TYPES = ["Buyer", "Investor", "Channel Partner"];
const EMPTY = { name: "", phone: "", email: "", city: "", user_type: "" };
const ORIGIN = API.replace(/\/api\/?$/, "");

const validate = (f) => {
    const e = {};
    if (f.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^\+?[0-9][0-9\s-]{6,16}$/.test(f.phone.trim()) || f.phone.replace(/\D/g, "").length < 10) e.phone = "Enter a valid mobile number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Enter a valid email address.";
    if (f.city.trim().length < 2) e.city = "Please enter your city.";
    if (!f.user_type) e.user_type = "Please select one.";
    return e;
};

const startDownload = (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
};

// Lead-gated brochure: the file is only released (via a short-lived link) after the lead is saved.
export default function BrochureDialog({ property, open, onOpenChange }) {
    const [f, setF] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [link, setLink] = useState(null);
    const upd = (k, v) => { setF((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };

    const close = (o) => { onOpenChange(o); if (!o) { setLink(null); setErrors({}); } };

    const submit = async (ev) => {
        ev.preventDefault();
        if (busy) return;
        const e = validate(f);
        setErrors(e);
        if (Object.values(e).some(Boolean)) return;
        setBusy(true);
        try {
            const r = await api.post(`/properties/${property.id}/brochure-request`, {
                name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim(), city: f.city.trim(),
                user_type: f.user_type, page_url: window.location.href,
            });
            const url = `${ORIGIN}${r.data.download_url}`;
            setLink(url);
            startDownload(url);
        } catch (err) {
            toast.error(err?.response ? formatApiError(err) : "Something went wrong. Please check your connection and try again.");
        } finally { setBusy(false); }
    };

    const input = "w-full border border-black/15 bg-white px-3 py-3 text-sm outline-none focus:border-[var(--ebg-gold)]";
    const err = (k) => errors[k] && <div className="text-[11px] text-red-600 mt-1" data-testid={`brochure-error-${k}`}>{errors[k]}</div>;

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-md rounded-none max-h-[92vh] overflow-y-auto" data-testid="brochure-dialog">
                {link ? (
                    <div className="text-center py-4" data-testid="brochure-success">
                        <CheckCircle2 size={34} className="mx-auto text-[var(--ebg-gold)]"/>
                        <DialogTitle className="font-serif text-2xl font-normal mt-3">Thank you.</DialogTitle>
                        <DialogDescription className="text-[14px] text-black/65 mt-2">Your brochure download is starting now.</DialogDescription>
                        <a href={link} data-testid="brochure-fallback-link" className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)] hover:text-black">
                            <Download size={13}/> Download didn&rsquo;t start? Click here
                        </a>
                    </div>
                ) : (
                    <form onSubmit={submit} noValidate className="space-y-3" data-testid="brochure-form">
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">{property.title}</div>
                        <DialogTitle className="font-serif text-2xl font-normal">Download Brochure</DialogTitle>
                        <DialogDescription className="text-[13px] text-black/60 -mt-1">Please share your details to access the project brochure.</DialogDescription>
                        <div><input placeholder="Full name *" value={f.name} onChange={(e) => upd("name", e.target.value)} className={input} data-testid="brochure-name"/>{err("name")}</div>
                        <div><input placeholder="Mobile number *" inputMode="tel" value={f.phone} onChange={(e) => upd("phone", e.target.value)} className={input} data-testid="brochure-phone"/>{err("phone")}</div>
                        <div><input placeholder="Email *" type="email" value={f.email} onChange={(e) => upd("email", e.target.value)} className={input} data-testid="brochure-email"/>{err("email")}</div>
                        <div><input placeholder="City *" value={f.city} onChange={(e) => upd("city", e.target.value)} className={input} data-testid="brochure-city"/>{err("city")}</div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">I am a *</div>
                            <div className="grid grid-cols-3 gap-2" role="radiogroup">
                                {USER_TYPES.map((t) => (
                                    <button type="button" key={t} role="radio" aria-checked={f.user_type === t} onClick={() => upd("user_type", t)}
                                        data-testid={`brochure-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                        className={`py-2.5 text-[11px] font-bold border transition-colors ${f.user_type === t ? "bg-black text-white border-black" : "border-black/15 text-black/70 hover:border-black"}`}>
                                        {t}
                                    </button>
                                ))}
                            </div>
                            {err("user_type")}
                        </div>
                        <button type="submit" disabled={busy} data-testid="brochure-submit" className="w-full bg-[var(--ebg-gold)] text-black py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                            {busy ? <><Loader2 size={13} className="animate-spin"/> Submitting…</> : "Submit & Download Brochure"}
                        </button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
